import TicketTemplate from "../../../../../Ticketing/TicketTemplate/TicketTemplate.js";
import useInternalManualTicketForm from "../../../../../Ticketing/InternalManualTicket/useInternalManualTicketForm.js";
import React, { useMemo } from "react";
import { useUserAnalytics, useMutationWrapper } from "@SeemplicityDev/shared";
import type { FetchResult } from "@apollo/client";
import { OPEN_TEST_TICKET } from "../../../../TicketingSystemsApi.js";
import useTicketManagers from "../../../../../Ticketing/useTicketManagers/useTicketManagers.js";
import { ticketEndpointContextEnum } from "../../../../../Ticketing/TicketingUtils.js";
import type {
  ManualTicketMappingData,
  ManualTicketError,
} from "../../../../../Ticketing/useManualTicket.js";

// Argument shape produced by TicketTemplate's `onCreateTicket` callback.
// `mutationData` is a JSON string forwarded verbatim into the mutation's
// `value_mapping` variable.
interface OnSubmitArgs {
  mutationData?: string;
}

// Shape of the OPEN_TEST_TICKET mutation result consumed below.
interface OpenTestTicketData {
  validate_open_ticket?: {
    result?: { result?: boolean; error?: string };
    ticket_link?: string;
    ticket_external_id?: string;
  };
}

export interface TestManualTicketProps {
  ticketProviderId?: string;
  ticketProviderType?: string;
  onSuccess?: (ticket: {
    link?: string;
    external_id?: string;
    ticketManagerFriendlyName?: string;
  }) => void;
  onError?: (error: { message?: string }) => void;
  // Supplied by useManualTicket; forwarded (via variadic adapters) to the
  // TicketTemplate child, whose onCreateMapping props are variadic.
  onCreateMapping?: (data?: ManualTicketMappingData) => void;
  onCreateMappingError?: (error?: ManualTicketError) => void;
  closeModal: () => void;
  // TODO: endpointConfig flows through to the untyped TicketTemplate child;
  // typed as unknown until that config shape is modeled.
  endpointConfig?: unknown;
}

const TestManualTicket: React.FC<TestManualTicketProps> = ({
  ticketProviderId,
  ticketProviderType,
  onSuccess = () => {},
  onError = () => {},
  onCreateMapping = () => {},
  onCreateMappingError = () => {},
  closeModal,
  endpointConfig,
}) => {
  const { trackEvent } = useUserAnalytics();

  const { setTicketTemplateValid, modalButtons, childRef, setIsSubmitting } =
    useInternalManualTicketForm({ closeModal });

  const { getTicketManagerProps } = useTicketManagers({
    context: ticketEndpointContextEnum.MANUAL_TICKET,
  });

  const [openTestTicket] =
    useMutationWrapper<OpenTestTicketData>(OPEN_TEST_TICKET);

  const onSubmit = ({ mutationData }: OnSubmitArgs) => {
    let variables = {
      value_mapping: mutationData,
      ticket_provider_id: ticketProviderId,
    };

    return openTestTicket({
      variables,
    })
      .then((res: FetchResult<OpenTestTicketData>) => {
        if (!!res?.data?.validate_open_ticket?.result?.result) {
          onSuccess({
            link: res?.data?.validate_open_ticket?.ticket_link,
            external_id: res?.data?.validate_open_ticket?.ticket_external_id,
            ticketManagerFriendlyName:
              getTicketManagerProps(ticketProviderType)?.friendlyName,
          });
          closeModal();
        } else {
          onError({ message: res?.data?.validate_open_ticket?.result?.error });
        }
      })
      .catch((err: { message?: string }) => {
        onError({ message: err.message });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const ticketManagerProps = useMemo(
    () => ({
      ...getTicketManagerProps(ticketProviderType),
      providers: [
        getTicketManagerProps(ticketProviderType)?.providers?.find(
          (provider) => provider?.id === ticketProviderId
        ),
      ],
    }),
    [getTicketManagerProps, ticketProviderType]
  );

  return (
    <TicketTemplate
      ticketManagerId={ticketProviderType}
      ticketProviderId={ticketProviderId}
      ticketManagerMustHaveColumnsMapping={
        ticketManagerProps?.mustHaveColumnsMapping
      }
      ticketManagerMustHaveMapping={ticketManagerProps?.mustHaveMapping}
      ticketManagerProviders={
        // providers is `(TicketManagerProvider | undefined)[]` (the find may
        // yield undefined); original JS forwarded it verbatim. Type-only cast.
        ticketManagerProps?.providers as Array<{ id?: string }>
      }
      ticketManagerFriendlyName={ticketManagerProps?.friendlyName}
      onCreateTicket={onSubmit}
      onCreateMapping={(...args: unknown[]) =>
        onCreateMapping(args[0] as ManualTicketMappingData)
      }
      onCreateMappingError={(...args: unknown[]) =>
        onCreateMappingError(args[0] as ManualTicketError)
      }
      onEventTriggered={(...args: unknown[]) =>
        // trackEvent is (eventName?, eventProps?) => void; TicketTemplate's
        // onEventTriggered is variadic. Adapter forwards args verbatim
        // (onEventTriggered is only ever called with an event-name string).
        trackEvent(args[0] as string, args[1] as Record<string, unknown>)
      }
      onValidationChange={(isValid: boolean) => {
        setTicketTemplateValid(isValid);
      }}
      ref={childRef}
      modalButtonsContainer={modalButtons}
      endpointConfig={endpointConfig}
    />
  );
};

export default TestManualTicket;
